import { createClient } from '@supabase/supabase-js'

const SUPABASE_URL     = process.env.REACT_APP_SUPABASE_URL
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY)

// ── Auth ──────────────────────────────────────────────────────────
export const signUp = (email, password, metadata) =>
  supabase.auth.signUp({ email, password, options: { data: metadata } })

export const signIn = (email, password) =>
  supabase.auth.signInWithPassword({ email, password })

export const signInWithGoogle = () =>
  supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: `${window.location.origin}/auth/callback` },
  })

export const signOut = () => supabase.auth.signOut()

export const getUser = () => supabase.auth.getUser()

export const resetPassword = (email) =>
  supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  })

// ── Profile ───────────────────────────────────────────────────────
export const getProfile = (userId) =>
  supabase.from('profiles').select('*').eq('id', userId).maybeSingle()

// ── Organizations ─────────────────────────────────────────────────
export const createOrganization = (data) =>
  supabase.from('organizations').insert([data]).select().single()

export const getOrganization = (adminId) =>
  supabase.from('organizations').select('*').eq('admin_id', adminId).maybeSingle()

export const getOrganizationByShortCode = (code) =>
  supabase.from('organizations').select('*').eq('short_code', code.toUpperCase()).single()

export const updateOrganization = (id, data) =>
  supabase.from('organizations').update(data).eq('id', id)

// ── Members ───────────────────────────────────────────────────────
export const getMembers = (orgId) =>
  supabase.from('members').select('*').eq('org_id', orgId).order('name')

export const addMember = (data) =>
  supabase.from('members').insert([data]).select().single()

export const updateMember = (id, data) =>
  supabase.from('members').update(data).eq('id', id)

export const deleteMember = (id) =>
  supabase.from('members').delete().eq('id', id)

export const searchMembers = (orgId, query) =>
  supabase
    .from('members')
    .select('*')
    .eq('org_id', orgId)
    .or(`name.ilike.%${query}%,member_id.ilike.%${query}%`)
    .limit(8)

// ── Sessions ──────────────────────────────────────────────────────
export const getSessions = (orgId) =>
  supabase.from('sessions').select('*, locations(*)').eq('org_id', orgId).order('start_time')

export const addSession = (data) =>
  supabase.from('sessions').insert([data]).select().single()

export const updateSession = (id, data) =>
  supabase.from('sessions').update(data).eq('id', id)

export const deleteSession = (id) =>
  supabase.from('sessions').delete().eq('id', id)

// ── Locations ─────────────────────────────────────────────────────
export const getLocations = (orgId) =>
  supabase.from('locations').select('*').eq('org_id', orgId).order('name')

export const addLocation = (data) =>
  supabase.from('locations').insert([data]).select().single()

export const updateLocation = (id, data) =>
  supabase.from('locations').update(data).eq('id', id)

export const deleteLocation = (id) =>
  supabase.from('locations').delete().eq('id', id)

// ── Attendance ────────────────────────────────────────────────────
export const getAttendance = (orgId, filters = {}) => {
  let q = supabase
    .from('attendance')
    .select('*, members(name, member_id), sessions(name, group_name)')
    .eq('org_id', orgId)
    .order('marked_at', { ascending: false })
  if (filters.sessionId) q = q.eq('session_id', filters.sessionId)
  if (filters.dateFrom)  q = q.gte('marked_at', filters.dateFrom + 'T00:00:00')
  if (filters.dateTo)    q = q.lte('marked_at', filters.dateTo + 'T23:59:59')
  if (filters.status)    q = q.eq('status', filters.status)
  if (filters.limit)     q = q.limit(filters.limit)
  return q
}

export const overrideAttendance = (data) =>
  supabase.from('attendance').insert([{ ...data, status: 'override' }]).select().single()

// ── Lecturer invites ──────────────────────────────────────────────
export const inviteLecturer = (orgId, email) =>
  supabase.from('lecturer_invites').insert([{ org_id: orgId, email }]).select().single()

export const getInvites = (orgId) =>
  supabase.from('lecturer_invites').select('*').eq('org_id', orgId).order('created_at', { ascending: false })

export const revokeInvite = (id) =>
  supabase.from('lecturer_invites').delete().eq('id', id)

export const getInviteByToken = (token) =>
  supabase.from('lecturer_invites').select('*, organizations(name, org_type)').eq('token', token).single()

export const acceptInvite = (token, lecturerId) =>
  supabase.from('lecturer_invites').update({ accepted: true, lecturer_id: lecturerId }).eq('token', token)

// ── Lecturer session assignments ──────────────────────────────────
export const assignLecturerToSession = (orgId, lecturerId, sessionId) =>
  supabase.from('lecturer_sessions').insert([{ org_id: orgId, lecturer_id: lecturerId, session_id: sessionId }])

export const removeLecturerFromSession = (lecturerId, sessionId) =>
  supabase.from('lecturer_sessions').delete().eq('lecturer_id', lecturerId).eq('session_id', sessionId)

export const getLecturerSessions = (lecturerId) =>
  supabase
    .from('lecturer_sessions')
    .select('*, sessions(*, locations(*), organizations(*))')
    .eq('lecturer_id', lecturerId)

// ── Activity log ──────────────────────────────────────────────────
export const logActivity = (orgId, actorId, actorName, action, detail) =>
  supabase.from('activity_log').insert([{ org_id: orgId, actor_id: actorId, actor_name: actorName, action, detail }])

export const getActivityLog = (orgId) =>
  supabase.from('activity_log').select('*').eq('org_id', orgId).order('created_at', { ascending: false }).limit(100)

// ── Contact messages ──────────────────────────────────────────────
export const submitContactMessage = (data) =>
  supabase.from('contact_messages').insert([data])

export const getContactMessages = () =>
  supabase.from('contact_messages').select('*').order('created_at', { ascending: false })

export const markReplied = (id) =>
  supabase.from('contact_messages').update({ replied: true }).eq('id', id)

// ── Superadmin queries ────────────────────────────────────────────
export const getAllOrganizations = () =>
  supabase.from('organizations').select('*, profiles(full_name)').order('created_at', { ascending: false })

export const getAllOrganizationsDetailed = () =>
  supabase
    .from('organizations')
    .select('*, profiles!admin_id(full_name, role)')
    .order('created_at', { ascending: false })

export const getAllAttendanceCount = () =>
  supabase.from('attendance').select('id', { count: 'exact', head: true })

export const getTodayAttendanceCount = () => {
  const today = new Date().toISOString().slice(0, 10)
  return supabase
    .from('attendance')
    .select('id', { count: 'exact', head: true })
    .gte('marked_at', today + 'T00:00:00')
    .lte('marked_at', today + 'T23:59:59')
}

// ── Plan pricing ──────────────────────────────────────────────────
export const getPlanPricing = () =>
  supabase.from('plan_pricing').select('*').order('price')

export const updatePlanPrice = (planKey, price) =>
  supabase
    .from('plan_pricing')
    .update({ price, updated_at: new Date().toISOString() })
    .eq('plan_key', planKey)

// ── Short code generator ──────────────────────────────────────────
export function generateShortCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let code = 'CF'
  for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)]
  return code
}

// ── Superadmin org management (audited) ───────────────────────────
// Single canonical definition. Called as:
//   superadminUpgradeOrg(orgId, updates, reason, user.id)
// Writes a permanent record to admin_actions for every change.
export const superadminUpgradeOrg = async (orgId, updates, reason, superadminId) => {
  const { data: before } = await supabase
    .from('organizations')
    .select('plan, subscription_status, trial_ends_at, semester_end')
    .eq('id', orgId)
    .single()

  const { data, error } = await supabase
    .from('organizations')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', orgId)
    .select()
    .single()

  if (error) return { data, error }

  await supabase.from('admin_actions').insert([{
    superadmin_id: superadminId,
    org_id:        orgId,
    action:        'superadmin_org_update',
    old_value:     before,
    new_value:     updates,
    reason,
  }])

  return { data, error }
}

// Delegates through superadminUpgradeOrg so audit trail is always consistent
export const superadminExtendTrial = async (orgId, days, reason, superadminId) => {
  const newEnd = new Date()
  newEnd.setDate(newEnd.getDate() + days)
  return superadminUpgradeOrg(
    orgId,
    { subscription_status: 'trial', trial_ends_at: newEnd.toISOString() },
    reason,
    superadminId,
  )
}

export const getAdminActions = () =>
  supabase
    .from('admin_actions')
    .select('*, organizations(name), profiles(full_name)')
    .order('created_at', { ascending: false })
    .limit(100)
